import { escapeFilterValue } from '../../lib/pocketbase'
/**
 * Reads run on the portal's own PocketBase client, not the agency singleton.
 *
 * `pb` from lib/pocketbase carries the AGENT's session under a different
 * localStorage key; in an athlete's browser it holds no token at all, and
 * PocketBase answers an unauthorised list with 200 and an empty array. The
 * writes in this file always used getClientPb() — they had to, or they would
 * have failed loudly — so the reads beside them were the half that went
 * quiet. getClientPb() carries the session the row rules are written against.
 */
import { getClientUser } from '../clientAuth'
import { getClientPb } from '../../lib/clientPocketbase'
import { loadClientDealTitles, dealTitle } from './deals'
import { isPreview, readOnlyGuard } from './previewMode'
import type {
  ClientActionItem,
  ClientActionItemSummary,
  ClientComplianceItem,
} from '../../types/clientPortal'

export async function getClientActionItems(): Promise<ClientActionItem[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const records = await getClientPb().collection('tasks').getFullList({
    filter: `client_id = "${escapeFilterValue(user.client_id)}" && visible_to_client = true`,
    sort: '-priority,due_date',
    expand: 'created_by',
  })
  await loadClientDealTitles()

  return records.map((record): ClientActionItem => {
    const createdByUser = record.expand?.created_by
    const deal = dealTitle(record.deal_id)

    return {
      id: record.id,
      title: record.title ?? null,
      description: record.description ?? null,
      client_instructions: record.client_instructions ?? null,
      status: record.status ?? null,
      priority: record.priority ?? null,
      task_category: record.task_category ?? null,
      due_date: record.due_date ?? null,
      location: record.location ?? null,
      event_time: record.event_time ?? null,
      completed_at: record.completed_at ?? null,
      entity_type: record.entity_type ?? null,
      entity_id: record.entity_id ?? record.deal_id ?? null,
      entity_title: deal?.title ?? null,
      client_acknowledged: record.client_acknowledged ?? false,
      client_acknowledged_at: record.client_acknowledged_at ?? null,
      created: record.created,
      updated: record.updated,
      created_by_name: createdByUser
        ? `${createdByUser.first_name ?? ''} ${createdByUser.last_name ?? ''}`.trim() || null
        : null,
    }
  })
}

export async function getClientUpcomingItems(): Promise<ClientActionItem[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const now = new Date()
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

  const records = await getClientPb().collection('tasks').getFullList({
    filter: `client_id = "${escapeFilterValue(user.client_id)}" && visible_to_client = true && status != "completed" && status != "cancelled" && due_date >= "${now.toISOString().split('T')[0]}" && due_date <= "${weekFromNow.toISOString().split('T')[0]}"`,
    sort: 'due_date',
    expand: 'created_by',
  })
  await loadClientDealTitles()

  return records.map((record): ClientActionItem => {
    const createdByUser = record.expand?.created_by
    const deal = dealTitle(record.deal_id)
    return {
      id: record.id,
      title: record.title ?? null,
      description: record.description ?? null,
      client_instructions: record.client_instructions ?? null,
      status: record.status ?? null,
      priority: record.priority ?? null,
      task_category: record.task_category ?? null,
      due_date: record.due_date ?? null,
      location: record.location ?? null,
      event_time: record.event_time ?? null,
      completed_at: record.completed_at ?? null,
      entity_type: record.entity_type ?? null,
      entity_id: record.entity_id ?? record.deal_id ?? null,
      entity_title: deal?.title ?? null,
      client_acknowledged: record.client_acknowledged ?? false,
      client_acknowledged_at: record.client_acknowledged_at ?? null,
      created: record.created,
      updated: record.updated,
      created_by_name: createdByUser
        ? `${createdByUser.first_name ?? ''} ${createdByUser.last_name ?? ''}`.trim() || null
        : null,
    }
  })
}

export async function getClientDealActionItems(dealId: string): Promise<ClientActionItem[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const records = await getClientPb().collection('tasks').getFullList({
    filter: `client_id = "${escapeFilterValue(user.client_id)}" && visible_to_client = true && deal_id = "${escapeFilterValue(dealId)}"`,
    sort: '-priority,due_date',
    expand: 'created_by',
  })

  return records.map((record): ClientActionItem => {
    const createdByUser = record.expand?.created_by
    return {
      id: record.id,
      title: record.title ?? null,
      description: record.description ?? null,
      client_instructions: record.client_instructions ?? null,
      status: record.status ?? null,
      priority: record.priority ?? null,
      task_category: record.task_category ?? null,
      due_date: record.due_date ?? null,
      location: record.location ?? null,
      event_time: record.event_time ?? null,
      completed_at: record.completed_at ?? null,
      entity_type: 'deal',
      entity_id: dealId,
      entity_title: null,
      client_acknowledged: record.client_acknowledged ?? false,
      client_acknowledged_at: record.client_acknowledged_at ?? null,
      created: record.created,
      updated: record.updated,
      created_by_name: createdByUser
        ? `${createdByUser.first_name ?? ''} ${createdByUser.last_name ?? ''}`.trim() || null
        : null,
    }
  })
}

export async function getClientActionItemSummary(): Promise<ClientActionItemSummary> {
  const items = await getClientActionItems()
  const now = new Date()
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

  const pendingItems = items.filter(
    (item) => item.status !== 'completed' && item.status !== 'cancelled'
  )

  const overdueCount = pendingItems.filter((item) => {
    if (!item.due_date) return false
    return new Date(item.due_date) < now
  }).length

  const dueThisWeek = pendingItems.filter((item) => {
    if (!item.due_date) return false
    const dueDate = new Date(item.due_date)
    return dueDate >= now && dueDate <= weekFromNow
  }).length

  const requiresAction = pendingItems.filter(
    (item) => item.task_category === 'action_required' || item.task_category === 'provide_info'
  ).length

  return {
    total_pending: pendingItems.length,
    overdue_count: overdueCount,
    due_this_week: dueThisWeek,
    requires_action: requiresAction,
  }
}

export async function acknowledgeActionItem(itemId: string): Promise<void> {
  readOnlyGuard('marking this seen for them')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const item = await getClientPb().collection('tasks').getOne(itemId)
  if (item.client_id !== user.client_id) throw new Error('Access denied')

  const clientPb = getClientPb()
  await clientPb.collection('tasks').update(itemId, {
    client_acknowledged: true,
    client_acknowledged_at: new Date().toISOString(),
  })
}

export async function completeActionItem(itemId: string): Promise<void> {
  readOnlyGuard('completing this for them')
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const item = await getClientPb().collection('tasks').getOne(itemId)
  if (item.client_id !== user.client_id) throw new Error('Access denied')

  const clientPb = getClientPb()
  await clientPb.collection('tasks').update(itemId, {
    status: 'completed',
    completed_at: new Date().toISOString(),
  })
}

export async function getClientComplianceItems(): Promise<ClientComplianceItem[]> {
  const user = getClientUser()
  if (!user) throw new Error('Client not authenticated')

  const records = await getClientPb().collection('compliance_items').getFullList({
    filter: `client_id = "${escapeFilterValue(user.client_id)}"`,
    sort: '+due_date',
    fields: 'id,title,description,type,status,due_date,completed_date,is_recurring,notes,created',
  })

  return records.map((r) => ({
    id: r.id,
    title: r.title,
    description: r.description || null,
    type: r.type || null,
    status: r.status || 'pending',
    due_date: r.due_date || null,
    completed_date: r.completed_date || null,
    is_recurring: r.is_recurring || null,
    notes: r.notes || null,
    created: r.created,
  }))
}
